import { Box, Divider, FormControl, FormLabel, Input, Typography } from "@mui/joy";
import { Link, Link2 } from "lucide-react";
import { Button, IconButton, Alert } from "../../../../../../shared";
import { useEventHandler } from "../../hooks";

/**
 * Responsive form row:
 *  - Desktop (≥md): side-by-side (label left 300px, input right)
 *  - Mobile/Tablet (<md): stacked (label on top, input below) — matches shared/TextField
 */
function FormRow({
    label,
    description,
    children,
}: {
    label: string;
    description?: string;
    children: React.ReactNode;
}) {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "stretch", md: "center" },
                gap: { xs: 0.5, md: 2 },
            }}
        >
            <Box sx={{ flexBasis: { md: 300 }, flexShrink: 0 }}>
                <FormLabel sx={{ m: 0 }}>{label}</FormLabel>
                {description && (
                    <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.25 }}>
                        {description}
                    </Typography>
                )}
            </Box>
            <Box sx={{ flex: 1 }}>{children}</Box>
        </Box>
    );
}

export function Basic({ details }: { details: any | null }) {
    const {
        slug,
        setSlug,
        projectType,
        setProjectType,
        status,
        setStatus,
        secondarySalesmanName,
        setSecondarySalesmanName,
        eagleViewOrderId,
        setEagleViewOrderId,
        saving,
        error,
        success,
        hasChanges,
        handleUpdate,
    } = useEventHandler(details);

    return (
        <Box sx={{ flex: 1 }}>
            <Box mb={1}>
                <Typography level="title-sm" component={"div"}>
                    Basic Information
                    <Typography level="body-xs" sx={{ opacity: 0.8, ml: 1 }} component={"span"}>
                        {details?.Id}
                    </Typography>
                </Typography>
                <Typography level="body-sm">You can edit the basic information of the project seamlessly.</Typography>
            </Box>

            {error && <Alert errorMessage={error} />}
            {success && <Alert successMessage={success} />}

            <Divider sx={{ mb: 3, mt: 2 }} />
            <Box display={"flex"} flexDirection={"column"} gap={2}>
                <FormRow label="EagleView Order ID" description="Project Id / EagleView order Id.">
                    <Input
                        disabled={!!details?.EagleViewOrderId}
                        value={eagleViewOrderId}
                        onChange={(e) => setEagleViewOrderId(e.target.value)}
                        size="sm"
                        variant="outlined"
                    />
                </FormRow>
                <FormRow label="Lead Source" description="Primary source of this lead.">
                    <Input
                        value={details?.Lead?.LeadSource?.Name ?? "Not specified"}
                        disabled
                        size="sm"
                        variant="outlined"
                    />
                </FormRow>
                {details?.Lead?.Name && (
                    <FormRow label="Secondary Lead" description="Person or company who brought this lead.">
                        <Input value={details.Lead.Name} disabled size="sm" variant="outlined" />
                    </FormRow>
                )}

                <FormRow label="Status" description="Current status of the project.">
                    <Input
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        size="sm"
                        variant="outlined"
                    />
                </FormRow>
                <Divider sx={{ my: 1 }} />

                <FormRow label="Project Slug" description="This will be used to access the project.">
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Input
                            startDecorator={<Link2 size={16} />}
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            size="sm"
                            variant="outlined"
                            sx={{ flex: 1 }}
                        />
                        <IconButton>
                            <Link size={16} />
                        </IconButton>
                    </Box>
                </FormRow>
                <FormRow label="Project Type" description="This will be used to access the project.">
                    <Input
                        value={projectType}
                        onChange={(e) => setProjectType(e.target.value)}
                        size="sm"
                        variant="outlined"
                    />
                </FormRow>
                <Divider sx={{ my: 1 }} />

                <FormRow label="Salesman Name" description="Primary sales representative.">
                    <Input value={details?.SalesmanName ?? ""} disabled size="sm" variant="outlined" />
                </FormRow>
                <FormRow label="Secondary Salesman Name" description="Additional sales representative.">
                    <Input
                        value={secondarySalesmanName}
                        onChange={(e) => setSecondarySalesmanName(e.target.value)}
                        size="sm"
                        variant="outlined"
                        placeholder="Enter secondary salesman name"
                    />
                </FormRow>

                <Box>
                    <Button
                        onClick={handleUpdate}
                        disabled={!hasChanges || saving}
                        loading={saving}
                        loadingPosition="end"
                    >
                        {saving ? "Updating" : "Update"}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
